import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { User } from '../../models/user/user';
import { AlertService } from '../../services/alert.service';
import { AuthService } from '../../services/auth.service';
import { RegexService } from '../../services/regex.service';
import { SeoService } from '../../services/seo.service';
import { ToolsService } from '../../services/tools.service';
import { UserService } from '../../services/user.service';
import { Store } from '../../store';
import { checkPasswords } from '../../utils/custom-validators';
import { CompanyData, DefaultCompanyData, UserAccountType } from '../../data-types/user-data';

@Component({
    selector: 'app-trade-account-registration-form',
    templateUrl: './trade-account-registration-form.component.html',
    styleUrls: ['./trade-account-registration-form.component.scss'],
})
export class TradeAccountRegistrationFormComponent implements OnInit {
    @Input() regPromoCode;

    userFromStorage: User = this.store.selectForLocal('user');

    referralCode;

    tradeAccount: boolean = true;
    form: FormGroup;
    validReferralCode = true;

    newUserId = '';
    newContactId = '';
    newCompanyId = '';
    newAddressId = '';
    user = {
        id: this.newUserId,
        username: '',
        password: '',
        confirmPassword: '',
        extendedExpiration: false,
        accountType: 'trade',
        terms: false,
        marketing: false,
        referralCode: '',
        regPromoCode: '',
        contact: {
            id: this.newContactId,
            userId: this.newUserId,
            addressId: '',
            companyId: '',
            firstname: '',
            surname: '',
            email: '',
            mobile: '',
            mobDialCode: '',
            landline: '',
            jobTitle: '',
            avatar: '',
        },
        source: '',
    };

    company: CompanyData = {
        ...DefaultCompanyData,
        id: this.newCompanyId,
        primaryUserId: this.newUserId
    };

    address: any = {
        id: this.newAddressId,
        userId: this.newUserId,
        add1: '',
        add2: '',
        add3: '',
        town: '',
        county: '',
        country: '',
        postcode: '',
        defaultDeliveryAddress: true,
        isBilling: true,
    };

    constructor(
        public regexService: RegexService,
        private store: Store,
        private fb: FormBuilder,
        private userService: UserService,
        private alertService: AlertService,
        private router: Router,
        private authService: AuthService,
        private toolsService: ToolsService,
        private seoService: SeoService
    ) {
        this.form = this.fb.group(
            {
                terms: [false, []],
                marketing: [false, []],
                user: fb.group({
                    firstname: ['', [Validators.required, Validators.minLength(2)]],
                    surname: ['', [Validators.required, Validators.minLength(2)]],
                    email: ['', [Validators.required, Validators.email]],
                    mobile: ['', [Validators.required, Validators.minLength(7)]],
                    password: [
                        '',
                        [
                            Validators.compose([
                                this.regexService.patternValidator(/\d/, { hasNumber: true }),
                                this.regexService.patternValidator(/[A-Z]/, { hasCapitalCase: true }),
                                this.regexService.patternValidator(/[a-z]/, { hasSmallCase: true }),
                                this.regexService.patternValidator(/[ !@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/, {
                                    hasSpecialCharacters: true,
                                }),
                                Validators.minLength(8),
                                Validators.required,
                                this.regexService.validatePasswordField(),
                            ]),
                        ],
                    ],
                    confirmPassword: ['', [Validators.required]],
                }),
                company: fb.group({
                    name: ['', [Validators.required, Validators.minLength(2)]],
                    url: ['', [Validators.minLength(2)]],
                    email: ['', [Validators.email]],
                    mobile: ['', [Validators.minLength(7)]],
                    companyNumber: ['', [Validators.minLength(2)]],
                    vatNumber: ['', [Validators.minLength(2)]],
                }),
            },
            {
                validators: [checkPasswords(['user'])],
            }
        );
    }

    ngOnInit(): void {
        this.seoService.updateTitle('Open a free trade account');
        this.seoService.addMetaTag(
            'description',
            'Open a free trade account today to access all kinds of exclusive benefits and discounts'
        );
        this.createPrimaryIds();
    }

    toggleValue(v) {
        this.user[v] = !this.user[v];
    }

    onSubmit() {
        if (this.form.valid) {
            this.user.password = this.form.value.user.password;
            this.user.username = this.form.value.user.email;

            this.user.contact.firstname = this.form.value.user.firstname;
            this.user.contact.surname = this.form.value.user.surname;
            this.user.contact.email = this.form.value.user.email;
            this.user.contact.mobile = this.form.value.user.mobile.e164Number;
            this.user.contact.mobDialCode = this.form.value.user.mobile.dialCode;
            /*this.user.contact.jobTitle = this.form.value.user.jobTitle;*/
            this.user.regPromoCode = this.regPromoCode;

            this.company.accountType = UserAccountType.Trade;
            this.company.name = this.form.value.company.name;
            this.company.companyNumber = this.form.value.company.companyNumber;
            this.company.vatNumber = this.form.value.company.vatNumber;
            this.company.email = this.form.value.company.email;
            if (this.form.value.company.mobile) {
                this.company.mobile = this.form.value.company.mobile.e164Number;
                this.company.mobDialCode = this.form.value.company.mobile.dialCode;
            }
            // this.company.landline = this.form.value.company.landline;

            this.store.set('confirmationEmail', this.user.contact.email);
            this.store.set('accountType', this.user.accountType);
            this.save();
        }
    }

    save() {
        this.user.contact.addressId = '';
        this.user.contact.companyId = this.newCompanyId;
        this.user.source = 'public';

        this.userService.createUser(this.user).subscribe(
            async (userData) => {
                this.user.id = userData.data.id;
                if (this.user.accountType !== 'customer') {
                    this.userService.createCompany(this.company, this.user).subscribe(
                        (data) => {
                            this.company.id = data.data.id;
                            this.goToConfirmationPage(this.user);
                        },
                        (error) => {
                            this.alertService.error([error.error.message]);
                        }
                    );
                }
            },
            (error) => {
                this.alertService.error([error.error.message]);
            }
        );
    }

    createPrimaryIds() {
        this.newUserId = this.toolsService.newUUID();
        this.newContactId = this.toolsService.newUUID();
        this.newAddressId = this.toolsService.newUUID();
        this.newCompanyId = this.toolsService.newUUID();

        this.user.id = this.newUserId;
        this.user.contact.id = this.newContactId;
        this.user.contact.userId = this.newUserId;

        this.company.id = this.newCompanyId;
        this.company.primaryUserId = this.newUserId;
        this.address.id = this.newAddressId;
    }

    goToConfirmationPage(user) {
        this.authService.login(user).subscribe(
            () => {
                this.userService.getByToken().subscribe((userData) => {
                    this.router.navigateByUrl('/account/confirmation');
                });
            },
            (error) => {
                this.alertService.error([error.error.message]);
            }
        );
    }
}
